import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BatchPagination from '../../../batch/BatchPagination';
import DataTable from '../../../common/DataTable';
import LoaderSpinner from '../../../common/Loader';
import ViewImage from '../../../common/ViewImage';
import SearchProduct from '../../../Inventory/Product/searchProduct';
import useActiveCompanySellerOptions from '../../../../hooks/useActiveCompanySellerOptions';
import {
  numOfPage,
  UnassignedReturnsColumns,
  UnassignedReasonOptions,
  UnassignedOcrStatusOptions,
} from '../../../../constant';
import { getUnassignedFilterOptionsService } from '../../../../services/unassignedReturns.service';
import UnassignedReturnsFilter from './UnassignedReturnsFilter';
import OcrModal from './OcrModal';
import ManualAssignModal from './ManualAssignModal';
import BulkResultModal from './BulkResultModal';
import SummaryCards from './SummaryCards';
import { useUnassignedFilters, useUnassignedFetch, useRowSelection } from './useUnassignedList';
import { useBulkOcr, useManualAssign } from './useUnassignedBulk';
import { AUTOMATED, MANUAL, getActiveCard } from './unassignedUtils';

const mapOptions = (list, fallback) => (
  Array.isArray(list) && list.length ? list.map((o) => ({ value: o.Value, label: o.Label })) : fallback
);

const useFilterOptions = () => {
  const [reasonOptions, setReasonOptions] = useState(UnassignedReasonOptions);
  const [ocrStatusOptions, setOcrStatusOptions] = useState(UnassignedOcrStatusOptions);

  useEffect(() => {
    getUnassignedFilterOptionsService()
      .then((res) => {
        setReasonOptions(mapOptions(res?.data?.Reasons, UnassignedReasonOptions));
        setOcrStatusOptions(mapOptions(res?.data?.OcrStatuses, UnassignedOcrStatusOptions));
      })
      .catch(() => {
        // Built-in options stay in place when the endpoint is unavailable.
      });
  }, []);

  return { reasonOptions, ocrStatusOptions };
};

const SubTabs = ({ action, counts, onChange }) => (
  <div className="package-assignment-tabs">
    <nav className="nav nav-tabs">
      <button className={`nav-link ${action === AUTOMATED ? 'active' : ''}`} onClick={() => onChange(AUTOMATED)}>
        Automated ({counts.automaticCount})
      </button>
      <button className={`nav-link ${action === MANUAL ? 'active' : ''}`} onClick={() => onChange(MANUAL)}>
        Manual ({counts.manualCount})
      </button>
    </nav>
  </div>
);

SubTabs.propTypes = {
  action: PropTypes.number.isRequired,
  counts: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

const BulkBar = ({ selectedCount, running, onOcr, onAssign }) => (
  <div className="unassigned-bulk-bar">
    <span className="unassigned-bulk-count">{selectedCount} selected</span>
    <button
      className="btn btn-primary btn-sm"
      onClick={onOcr}
      disabled={!selectedCount || running}
      title="Up to 25 packages per run"
    >
      Run OCR &amp; Auto-Match
    </button>
    <button className="btn btn-outline-primary btn-sm" onClick={onAssign} disabled={!selectedCount || running}>
      Bulk Assign Manually
    </button>
  </div>
);

BulkBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  running: PropTypes.bool,
  onOcr: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
};

const UnassignedReturns = ({ summary, onRefreshSummary, onAssignedChange, onOpenPackage }) => {
  const { companyOptions, sellerOptions } = useActiveCompanySellerOptions();
  const { reasonOptions, ocrStatusOptions } = useFilterOptions();
  const filters = useUnassignedFilters(reasonOptions);
  const { draft, setDraft, applied } = filters;
  const list = useUnassignedFetch(applied, onRefreshSummary);
  const { selectedRows, handleCheckRecord } = useRowSelection(list.rows, list.setRows);

  const [ocrRow, setOcrRow] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const shared = { selectedRows, refreshAll: list.refreshAll, onAssignedChange, showResult: setBulkResult };
  const { bulkRunning, handleBulkOcr } = useBulkOcr(shared);
  const manual = useManualAssign(shared);

  const handleAction = (item, type) => {
    if (type === 'thumbnail') {
      setPreviewRow({ ...item, imageUrl: item.imageUrl || item.thumbnailUrl });
      // The table invokes the returned value for thumbnail clicks.
      return () => {};
    }
    const actions = {
      ocr: () => setOcrRow(item),
      assign: () => manual.setAssignRows([item]),
      view: () => onOpenPackage(item, 'view'),
      edit: () => onOpenPackage(item, 'edit'),
    };
    actions[type]?.();
    return undefined;
  };

  const handleOcrHide = (changed) => {
    setOcrRow(null);
    if (changed) list.refreshAll();
  };

  const handleOcrAssigned = () => {
    setOcrRow(null);
    list.refreshAll();
    onAssignedChange();
  };

  const totalPages = Math.max(1, Math.ceil(list.totalRecords / applied.pageSize));

  return (
    <>
      {(list.status === 'PENDING' || bulkRunning) && (
        <div className="full-screen-spinner-overlay"><LoaderSpinner /></div>
      )}

      <SummaryCards summary={summary} activeCard={getActiveCard(applied.reasons)} onCardClick={filters.handleCardClick} />

      <div className="show searchBox mb-2">
        <SearchProduct
          showSearch={true}
          searchTxt={draft.searchKeywords}
          setSearchTxt={(text) => setDraft((prev) => ({ ...prev, searchKeywords: text }))}
          handleReset={filters.handleSearch}
          handleSearchFilter={filters.handleSearch}
          placeHolderText="Search Tracking Number, Carrier, Package Type, Source or Notes text"
        />
      </div>

      <div className="zIndex2">
        <UnassignedReturnsFilter
          draft={draft}
          setDraft={setDraft}
          reasonOptions={reasonOptions}
          ocrStatusOptions={ocrStatusOptions}
          onApply={filters.handleApply}
          onClear={filters.handleClear}
        />
      </div>

      <SubTabs action={applied.action} counts={list.subTabCounts} onChange={filters.handleSubTab} />

      <BulkBar
        selectedCount={selectedRows.length}
        running={bulkRunning}
        onOcr={handleBulkOcr}
        onAssign={manual.openBulkAssign}
      />

      <div className="main_content cus-page-content-sec zIndex1">
        <div className="main_content_table_history">
          <div className="incomingTbl tableFixHead">
            <DataTable
              tableData={list.rows}
              headers={UnassignedReturnsColumns}
              handleSortingColumn={filters.handleSort}
              tableDataStatus={list.status}
              filterData={applied}
              handleAction={handleAction}
              handleCheckRecord={handleCheckRecord}
              isSelectAll={list.rows.length > 0 && selectedRows.length === list.rows.length}
              actionType={15}
            />
          </div>
        </div>

        <BatchPagination
          gridData={list.rows}
          totalVisualRecords={list.rows.length}
          numberOfPage={applied.pageSize}
          numOfPage={numOfPage}
          loader={list.status === 'PENDING'}
          currentPage={applied.pageIndex}
          totalPages={totalPages}
          totalRecords={list.totalRecords}
          setCurrentPage={filters.setPageIndex}
          setNumberOfPage={filters.setPageSize}
        />
      </div>

      <OcrModal
        show={Boolean(ocrRow)}
        row={ocrRow}
        companyOptions={companyOptions}
        sellerOptions={sellerOptions}
        onHide={handleOcrHide}
        onAssigned={handleOcrAssigned}
      />

      <ManualAssignModal
        show={manual.assignRows.length > 0}
        rows={manual.assignRows}
        companyOptions={companyOptions}
        sellerOptions={sellerOptions}
        onHide={manual.closeAssign}
        onDone={manual.handleAssignDone}
      />

      <BulkResultModal
        show={Boolean(bulkResult)}
        title={bulkResult?.title || ''}
        result={bulkResult?.result}
        onHide={() => setBulkResult(null)}
      />

      {previewRow && (
        <ViewImage
          selectedRecord={previewRow}
          showHideImageModal={Boolean(previewRow)}
          setShowHideImageModal={() => setPreviewRow(null)}
          title="Preview"
        />
      )}
    </>
  );
};

UnassignedReturns.propTypes = {
  summary: PropTypes.object,
  onRefreshSummary: PropTypes.func.isRequired,
  onAssignedChange: PropTypes.func.isRequired,
  onOpenPackage: PropTypes.func.isRequired,
};

export default UnassignedReturns;
